import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { sanitizePostHtml, renderMarkdownLite } from '@/lib/sanitize';

interface Term {
  id: number;
  type: string;
  title: string;
  content: string;
  effective_date: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * 약관 뷰어 — type 별 최신 활성 버전을 GET /terms/:type 으로 가져와 렌더.
 * - 본문은 whitespace-pre-wrap (개행 보존, 별도 마크다운 없음)
 * - 미등록 시 안내 카피
 */
export function TermViewer({ type, fallbackTitle }: { type: string; fallbackTitle: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['terms', type],
    queryFn: () => get<{ term: Term }>(`/terms/${type}`),
    retry: (count, err: unknown) => {
      const e = err as { response?: { status?: number } };
      if (e?.response?.status === 404) return false;
      return count < 2;
    },
    staleTime: 5 * 60_000,
  });

  /* HTML 본문 — 어드민 RichTextEditor 출력은 백엔드에서 sanitize 됐지만
     렌더 시점에 한 번 더 화이트리스트 정제. 마크다운-lite(개행/굵게)도 함께 처리해
     기존 plain-text 시드(\n 단락) 호환. */
  const safeContent = useMemo(() => {
    const raw = data?.term.content ?? '';
    return sanitizePostHtml(renderMarkdownLite(raw));
  }, [data?.term.content]);

  return (
    <div className="container-base pt-4 pb-6">
      <div className="w-full rounded-2xl border border-[var(--color-border)] bg-white p-6 sm:p-8">
        {isLoading ? (
          <>
            <Skeleton className="mb-3 h-9 w-1/3" />
            <Skeleton className="h-72 w-full" />
          </>
        ) : isError || !data ? (
          <>
            <h1 className="text-3xl font-extrabold">{fallbackTitle}</h1>
            <p className="mt-4 text-sm text-[var(--color-text-sub)]">
              약관이 등록되지 않았어요. 운영자가 곧 게시할 예정이에요.
            </p>
          </>
        ) : (
          <>
            <header className="mb-6">
              <h1 className="text-3xl font-extrabold">{data.term.title}</h1>
            </header>
            <div
              className="prose prose-sm max-w-none post-content term-content text-[var(--color-text)]"
              dangerouslySetInnerHTML={{ __html: safeContent }}
            />
          </>
        )}
      </div>
    </div>
  );
}
